import React from 'react';

interface OrderSummaryProps {
  planType: 'individual' | 'business';
  billingFrequency: 'annual' | 'monthly';
  promoCodeApplied?: boolean;
  promoCodeDiscount?: number;
  totalPrice?: number; // 可选的总价，如果提供则使用此价格而不是内部计算
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  planType,
  billingFrequency,
  promoCodeApplied = false,
  promoCodeDiscount = 0,
  totalPrice
}) => {
  // 价格计算逻辑
  const getBasePrice = () => {
    if (planType === 'business') {
      return billingFrequency === 'annual' ? 30 : 35;
    } else {
      return billingFrequency === 'annual' ? 6.67 : 8; // 6.67 * 12 = 80.04
    }
  };

  const getAnnualTotal = () => {
    if (planType === 'business') {
      return 360; // $30 * 12 months (discounted price)
    } else {
      return 80; // $6.67 * 12 months (discounted price)
    }
  };

  const getAnnualFullPrice = () => {
    if (planType === 'business') {
      return 420; // $35 * 12 months (full price without discount)
    } else {
      return 96; // $8 * 12 months (full price without discount)
    }
  };

  const getAnnualDiscount = () => {
    if (planType === 'business') {
      // Business plan: $35/month * 12 = $420, but charged $360, so discount is $60
      return 60;
    } else {
      // Individual plan: $8/month * 12 = $96, but charged $80, so discount is $16
      return 16;
    }
  };

  const getAnnualDiscountPercentage = () => {
    if (planType === 'business') {
      // Business plan: $60 discount / $420 total = 14.29% ≈ 15%
      return 14;
    } else {
      // Individual plan: $16 discount / $96 total = 16.67% ≈ 17%
      return 17;
    }
  };

  const getFinalTotal = () => {
    console.log('totalPrice', totalPrice);
    // 如果提供了totalPrice，直接使用（PaymentPage.js已经计算好了正确的总价）
    if (totalPrice !== undefined) {
      // 对 totalPrice 也进行四舍五入处理，确保显示 $80.00 而不是 $80.04
      return promoCodeApplied ? totalPrice : Math.round(totalPrice);
    }
    
    // 否则使用内部计算逻辑（备用方案）
    if (billingFrequency === 'annual') {
      const monthlyPrice = getBasePrice();
      const annualTotal = monthlyPrice * 12;
      // 在计算年度总价时进行四舍五入，确保 $6.67 × 12 = $80.00
      const roundedAnnualTotal = Math.round(annualTotal * 100) / 100;
      return promoCodeApplied ? roundedAnnualTotal - promoCodeDiscount : roundedAnnualTotal;
    } else {
      const monthlyPrice = getBasePrice();
      return promoCodeApplied ? monthlyPrice - promoCodeDiscount : monthlyPrice;
    }
  };

  const getPlanDisplayName = () => {
    return planType === 'business' ? 'Business' : 'Individual';
  };

  const getBillingDisplayName = () => {
    return billingFrequency === 'annual' ? 'Annual' : 'Monthly';
  };

  return (
    <div style={{
      backgroundColor: '#F6F6F6',
      borderRadius: '8px',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Title */}
      <div style={{
        fontSize: '18px',
        fontWeight: 600,
        marginBottom: '8px',
        color: '#1F2937'
      }}>
        Order Summary
      </div>

      {/* Subtitle */}
      <div style={{
        fontSize: '14px',
        fontWeight: 600,
        color: '#222',
        marginBottom: '20px'
      }}>
        Zeitro {getPlanDisplayName()} Plan
      </div>

      {/* Price Details */}
      <div style={{ marginBottom: '20px' }}>
        {billingFrequency === 'annual' ? (
          <>
            {/* Annual pricing breakdown */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '16px', fontWeight: 400, color: '#000' }}>
                Annual (${(planType === 'business' ? 35 : 8).toFixed(2)} x 12 months)
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#000' }}>
                ${getAnnualFullPrice().toFixed(2)}
              </div>
            </div>

            {/* Annual discount */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '16px', fontWeight: 400, color: '#000' }}>
                Annual discount (Save {getAnnualDiscountPercentage()}%)
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#1F2890' }}>
                -${getAnnualDiscount().toFixed(2)}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Monthly pricing */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '14px', color: '#374151' }}>
                {getBillingDisplayName()} subscription
              </div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                ${getBasePrice().toFixed(2)}
              </div>
            </div>
          </>
        )}

        {/* Promo code discount if applied */}
        {promoCodeApplied && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <div style={{ fontSize: '14px', color: '#374151' }}>
              Promo code
            </div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: '#10B981' }}>
              -${promoCodeDiscount.toFixed(2)}
            </div>
          </div>
        )}
      </div>

      {/* Total */}
      <div style={{
        borderTop: '1px solid #E5E7EB',
        paddingTop: '16px',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1F2937' }}>
            Today's total
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1F2937' }}>
            ${getFinalTotal().toFixed(2)}
          </div>
        </div>
      </div>

      {/* Description */}
      <div style={{
        fontSize: '12px',
        color: '#6B7280',
        lineHeight: '16px',
        borderTop: '1px solid #E5E7EB',
        paddingTop: '16px'
      }}>
        Local taxes may apply based on your billing information. Final charges may vary and will be detailed in your email invoice. By clicking Continue, you authorize us to: 1) securely store your payment method; 2) update the billing amount based on the number of Paid Tier users and the usage of the Automated Soft Credit Pull feature; 3) automatically charge you at the end of every billing cycle.
      </div>
    </div>
  );
};

export default OrderSummary;

/*
使用示例：

// 在 PaymentPage.js 中使用
import OrderSummary from './orderSummary';

// 在组件中
<OrderSummary 
  planType={selectedPlan.id === 'business' ? 'business' : 'individual'}
  billingFrequency={isChecked ? 'annual' : 'monthly'}
  promoCodeApplied={promoCodeApplied}
  promoCodeDiscount={promoCodeApplied ? parseFloat(getUSD()) - parseFloat(getTotal()) : 0}
/>

// 或者创建一个更简化的版本，直接传递现有状态
<OrderSummary 
  planType={selectedPlan.id}
  billingFrequency={isChecked ? 'annual' : 'monthly'}
  promoCodeApplied={promoCodeApplied}
  promoCodeDiscount={promoCodeApplied ? parseFloat(getUSD()) - parseFloat(getTotal()) : 0}
/>
*/
